import { Typography } from '@mui/material';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useState } from 'react';

interface TableHeader {
  key: string;
  label: string;
  type?: 'money' | 'percentage';
}

const PRODUCT_TYPE_CONFIG = {
  factoring: {
    showStakeholderConcentrations: true,
    showSubProductConcentrations: true,
    defaultExpanded: 'stakeholder',
    title: 'Concentraciones',
    tabs: [
      { key: 'stakeholder', label: 'Deudor' },
      { key: 'subproduct', label: 'Subproductos' },
    ],
    subProductHeaders: [
      { key: 'name', label: 'Producto' },
      { key: 'advance', label: 'Anticipo' },
      { key: 'spread', label: 'Spread' },
      { key: 'arrearRate', label: 'Tasa mora' },
      { key: 'maxFinancingDays', label: 'Días' },
      { key: 'operationCostFixed', label: 'C. fijo', type: 'money' },
      { key: 'operationCostVariable', label: 'C. variable' },
      { key: 'amount', label: 'Monto', type: 'money' },
    ] as TableHeader[],
    stakeholderHeaders: [
      { key: 'businessName', label: 'Nombre' },
      { key: 'businessIdentifier', label: 'Rut' },
      { key: 'amount', label: 'Monto', type: 'money' },
    ] as TableHeader[],
  },
  sgr: {
    showStakeholderConcentrations: false,
    showSubProductConcentrations: true,
    defaultExpanded: 'subproduct',
    title: 'Concentraciones',
    tabs: [{ key: 'subproduct', label: 'Subproductos' }],
    subProductHeaders: [
      { key: 'name', label: 'Producto' },
      {
        key: 'operationCostFixed',
        label: 'Componente fijo',
        type: 'money-uf',
      },
      {
        key: 'fixedDiscountRate',
        label: 'Descuento fijo',
        type: 'percentage-soft',
      },
      {
        key: 'operationCostVariable',
        label: 'Componente variable',
        type: 'percentage-soft',
      },
      {
        key: 'variableDiscountRate',
        label: 'Descuento variable',
        type: 'percentage-soft',
      },
      { key: 'amount', label: 'Monto', type: 'money' },
    ] as TableHeader[],
    stakeholderHeaders: [
      { key: 'businessName', label: 'Nombre' },
      { key: 'businessIdentifier', label: 'Rut' },
      { key: 'amount', label: 'Monto', type: 'money' },
    ] as TableHeader[],
  },
};

type ProductTypeId = keyof typeof PRODUCT_TYPE_CONFIG;

interface ComponentsProps {
  subProductsConcentrations?: any[];
  stakeholdersConcentrations?: any[];
  productType: ProductTypeId;
}

const SubProductConcentrationContainer = (props: ComponentsProps) => {
  const { subProductsConcentrations, productType } = props;
  const config = PRODUCT_TYPE_CONFIG[productType as ProductTypeId];

  return (
    <div style={{ padding: '10px 0px' }}>
      <EnhancedTable
        dataHeaders={config.subProductHeaders}
        data={subProductsConcentrations ?? []}
      />
    </div>
  );
};

const StakeholderConcentrationContainer = (props: ComponentsProps) => {
  const { stakeholdersConcentrations, productType } = props;
  const config = PRODUCT_TYPE_CONFIG[productType as ProductTypeId];

  return (
    <div style={{ padding: '10px 0px' }}>
      <EnhancedTable
        dataHeaders={config.stakeholderHeaders}
        data={stakeholdersConcentrations ?? []}
      />
    </div>
  );
};

const CreditLineConcentrations = (props: ComponentsProps) => {
  const { stakeholdersConcentrations, subProductsConcentrations, productType } =
    props;

  if (!productType || !PRODUCT_TYPE_CONFIG[productType as ProductTypeId]) {
    return null;
  }

  const config = PRODUCT_TYPE_CONFIG[productType as ProductTypeId];
  const [concentrationExpanded, setConcentrationExpanded] = useState(
    config.defaultExpanded,
  );

  const hasMultipleTabs = config.tabs.length > 1;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body1">
          <b>{config.title}</b>
        </Typography>
        {hasMultipleTabs && (
          <div style={{ display: 'flex' }}>
            {config.tabs.map((tab, index) => (
              <Typography
                key={tab.key}
                variant="caption"
                color={concentrationExpanded !== tab.key ? 'primary' : 'grey'}
                style={{
                  marginLeft: index > 0 ? 10 : 0,
                  cursor:
                    concentrationExpanded !== tab.key ? 'pointer' : 'default',
                }}
                onClick={() => setConcentrationExpanded(tab.key)}
              >
                {tab.label}
              </Typography>
            ))}
          </div>
        )}
      </div>

      {config.showStakeholderConcentrations &&
        (concentrationExpanded === 'stakeholder' || !hasMultipleTabs) && (
          <StakeholderConcentrationContainer
            stakeholdersConcentrations={stakeholdersConcentrations}
            productType={productType}
          />
        )}

      {config.showSubProductConcentrations &&
        (concentrationExpanded === 'subproduct' || !hasMultipleTabs) && (
          <SubProductConcentrationContainer
            subProductsConcentrations={subProductsConcentrations}
            productType={productType}
          />
        )}
    </div>
  );
};

export default CreditLineConcentrations;
